// Apply Edge — About page. Port of design/source/public/about.html.
// Chrome (NavBar/Footer) is owned by HomeLayout — do not render here.
import { t, Trans } from "@lingui/macro";
import { ArrowRightIcon, EnvelopeIcon, GlobeIcon, MapPinIcon } from "@phosphor-icons/react";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

const getPrinciples = () =>
  [
    {
      n: "01",
      t: t`Substance over fluff`,
      b: t`We don't generate buzzword soup. Every bullet on a tailored resume points to specific evidence in your real work history. If we can't ground a claim, we don't write it.`,
    },
    {
      n: "02",
      t: t`Speed without spam`,
      b: t`We help you send sharper applications — tailored to roles that actually fit, only after your job profile is properly defined. We'd rather you send 25 thoughtful applications a week than 250 bad ones.`,
    },
    {
      n: "03",
      t: t`Your data, your call`,
      b: t`We don't sell data. You choose when to send an application. Auto-approve is an opt-in beta feature. You can export and delete everything from Settings → Data anytime.`,
    },
    {
      n: "04",
      t: t`Honest about AI`,
      b: t`AI drafts. You read, edit, and own the result. Review each application before sending, or choose to turn on auto-approve (beta).`,
    },
    {
      n: "05",
      t: t`Built on how hiring actually works`,
      b: t`Our ATS scoring and tailoring engine is trained on real recruiter feedback and parsing-system behavior — not guesswork. Keyword density, section ordering, format compatibility: every recommendation traces back to how applicant tracking systems actually read your resume.`,
    },
    {
      n: "06",
      t: t`Encrypted at every layer`,
      b: t`Credentials are AES-256 encrypted at rest. All traffic is TLS 1.3. Sensitive fields (EEO data, salary expectations) are stored in isolated encrypted blobs that even our own team can't read without your session key.`,
    },
  ] as const;

// Production figures as of 19 October 2026; update together with the visible date caption.
const getNumbers = (): readonly (readonly [string, string])[] => [
  ["196,000+", t`Jobs tracked`],
  ["74,000+", t`New jobs in the last 30 days`],
  ["225,000+", t`AI match scores`],
  ["1,300+", t`Applications prepared`],
];

export const AboutPage = () => (
  <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
    <SEO
      title={t`About — Apply Edge`}
      description={t`Apply Edge helps you find jobs, tailor resumes, and prepare applications with AI.`}
      canonical="/about"
    />

    {/* Hero */}
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1280px] px-6 py-24">
        <span className="text-xs font-medium text-muted-foreground">
          <Trans>About</Trans>
        </span>
        <h1 className="mt-4 text-[40px] font-semibold leading-[1.05] tracking-tight text-foreground duration-700 animate-in fade-in slide-in-from-bottom-4 sm:text-[48px] md:text-[68px]">
          <Trans>Job hunting</Trans>
          <br />
          <span className="font-serif font-normal italic text-muted-foreground">
            <Trans>shouldn&apos;t suck.</Trans>
          </span>
        </h1>
        <p className="mt-8 max-w-[60ch] text-[17px] leading-[1.55] text-foreground/85">
          <Trans>
            Applying to jobs can be the worst part of building a career — repetitive, opaque,
            soul-crushing. Every application asks for a slightly different resume. Formatting can
            trip up an ATS. Recruiters can ghost.
          </Trans>
        </p>
        <p className="mt-5 max-w-[60ch] text-[15px] leading-[1.6] text-muted-foreground">
          <Trans>
            We built the tool we needed: an honest AI assistant that drafts, scores, tailors, and
            tracks. You can review and send each application, or opt in to auto-approve (beta).
          </Trans>
        </p>
      </div>
    </section>

    {/* Numbers */}
    <section className="border-b border-border bg-muted/30">
      <div className="mx-auto max-w-[1280px] px-6 py-12">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {getNumbers().map(([n, l]) => (
            <div
              key={l}
              className="border-l border-border pl-5 transition-all duration-150 ease-out hover:-translate-y-[1px]"
            >
              <div className="text-[36px] font-semibold tracking-tight text-foreground">{n}</div>
              <div className="mt-1 text-xs font-medium text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          <Trans>As of October 2026</Trans>
        </p>
      </div>
    </section>

    {/* Principles */}
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <span className="text-xs font-medium text-muted-foreground">
          <Trans>Principles</Trans>
        </span>
        <h2 className="mt-2 max-w-[20ch] text-[36px] font-semibold leading-[1.15] tracking-tight text-foreground sm:text-[44px] md:leading-normal">
          <Trans>What we won&apos;t compromise on.</Trans>
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-2">
          {getPrinciples().map((p) => (
            <div key={p.n} className="border-t border-border pt-8">
              <div className="text-xs font-medium text-muted-foreground">{p.n}</div>
              <h3 className="mt-3 text-[24px] font-semibold tracking-tight text-foreground">
                {p.t}
              </h3>
              <p className="mt-4 text-[15px] leading-[1.7] text-muted-foreground">{p.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Where we are */}
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1fr_1fr]">
          <div>
            <span className="text-xs font-medium text-muted-foreground">
              <Trans>About the team</Trans>
            </span>
            <h2 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-tight text-foreground md:leading-normal">
              <Trans>Built for a global job search.</Trans>
            </h2>
            <p className="mt-5 text-[14.5px] leading-[1.65] text-muted-foreground">
              <Trans>
                Apply Edge helps job seekers discover roles across global and Gulf job boards,
                tailor their resumes, and keep applications organized.
              </Trans>
            </p>
            <p className="mt-5 text-[14.5px] leading-[1.65] text-muted-foreground">
              <Trans>
                We launched publicly on 31 May 2026. In 2026, we added GulfTalent and Wuzzuf to help
                people find more opportunities across the Gulf and Egypt.
              </Trans>
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-[14px] font-medium text-primary-foreground shadow-sm transition duration-150 ease-out hover:-translate-y-[1px] hover:bg-primary/90 hover:shadow-md"
            >
              <Trans>Get in touch</Trans>
              <ArrowRightIcon size={14} />
            </Link>
          </div>
          <div className="rounded-2xl border border-border bg-surface p-8">
            <div className="text-xs font-medium text-muted-foreground">
              <Trans>Apply Edge</Trans>
            </div>
            <h3 className="mt-2 text-[22px] font-semibold tracking-tight text-foreground">
              <Trans>Where to find us</Trans>
            </h3>
            <dl className="mt-6 space-y-5 text-[13.5px]">
              <div className="grid grid-cols-[24px_1fr] gap-3">
                <dt className="text-muted-foreground">
                  <MapPinIcon size={16} />
                </dt>
                <dd className="text-foreground">
                  <Trans>Online, worldwide</Trans>
                </dd>
              </div>
              <div className="grid grid-cols-[24px_1fr] gap-3">
                <dt className="text-muted-foreground">
                  <EnvelopeIcon size={16} />
                </dt>
                <dd className="text-foreground">
                  <a href="mailto:hello@apply-edge.com" className="hover:underline">
                    hello@apply-edge.com
                  </a>
                  <br />
                  <span className="text-[12.5px] text-muted-foreground">
                    <Trans>Get in touch by email</Trans>
                  </span>
                </dd>
              </div>
              <div className="grid grid-cols-[24px_1fr] gap-3">
                <dt className="text-muted-foreground">
                  <GlobeIcon size={16} />
                </dt>
                <dd className="text-foreground">
                  <Trans>apply-edge.com</Trans>
                  <br />
                  <span className="text-[12.5px] text-muted-foreground">
                    <Trans>Available to job seekers worldwide.</Trans>
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  </div>
);
